import { Component, OnInit } from '@angular/core';
import { MatSidenav } from '@angular/material/sidenav';
import { NavigationEnd, Router } from '@angular/router';
import { MediaChange, MediaObserver } from '@ngbracket/ngx-layout';
import { Store } from '@ngrx/store';
import { combineLatest, EMPTY, Observable } from 'rxjs';
import {
  catchError,
  concatMap,
  distinctUntilChanged,
  filter,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';

import { DestroySubscriptionsService } from '@libs/src/destroy-subscription/destroy-subscription.service';
import { GroupsBackend } from '@libs/src/groups/groups.backend';
import { HttpCacheService } from '@libs/src/http-caching/http-cache.service';
import { LocaleBackend } from '@libs/src/locale/locale.backend';
import { PlatformConfigHubSyncService } from '@libs/src/platform-config/platform-config-hub-sync.service';
import { GroupViewNavigationService } from '@main-client/src/app/group-view/group-view-navigation.service';
import { TranslationLocalStorageService } from '@main-client/src/app/translation-local-storage/translation-local-storage.service';
import { UserService } from '@main-client/src/app/user/user.service';

import { MostReadableColorPipe } from '@main-client/src/app/pipes/most-readable-color/most-readable-color.pipe';

import { NavigationLink } from './group-view-navigation.types';
import { UpdateGroupConfig } from '@libs/src/groups/groups-store/groups.actions';
import { HttpRequestCache } from '@libs/src/http-caching/http-cache.decorator';
import { UserContentTranslation } from '@libs/src/locale-lookup/locale-translations-map.interface';
import { GroupLocalesResponse } from '@libs/src/locale/locale.interface';
import { Account } from '@libs/src/models/account.model';
import { Group } from '@libs/src/models/group.model';
import { Tenant } from '@libs/src/tenant/tenant.interface';
import { trackBy } from '@libs/src/track-by/track-by';
import { IAppState, ISideNavState } from '@main-client/src/app/app.state';
import { FeedPopulate } from '@main-client/src/app/feed/feed.actions';
import { groupViewAnimations } from '@main-client/src/app/group-view/group-view.animations';
import { currentGroupSource } from '@main-client/src/app/group-view/group-view.helper';
import {
  isAdminOfGroup,
  isTenThousandCoffeesAdmin,
} from '@main-client/src/app/user/user.helpers';

import isEmpty from 'lodash-es/isEmpty';

const smallScreenSizes = ['xs', 'sm'];

@Component({
  animations: groupViewAnimations,
  providers: [DestroySubscriptionsService],
  selector: 'app-group-view',
  templateUrl: './group-view.component.html',
})
export class GroupViewComponent implements OnInit {
  tenantState = this.store.select('tenant');
  userState = this.store.select('user');
  sideNav$: Observable<ISideNavState> = this.store.select('sideNav');
  group: Group;
  links: NavigationLink[] = [];
  linksActive: { [key: string]: boolean } = {};
  showGroups = false;
  isSmallWindow = false;
  textColor: string;
  user: Account;
  trackByNavigationLinkTitle = trackBy(['title']);

  constructor(
    public readonly cache: HttpCacheService,
    private readonly destroy$: DestroySubscriptionsService,
    private readonly groupsBackend: GroupsBackend,
    private readonly groupViewNavigationService: GroupViewNavigationService,
    private readonly localeBackend: LocaleBackend,
    private readonly media: MediaObserver,
    private readonly mostReadableColor: MostReadableColorPipe,
    private readonly platformConfigHubSyncService: PlatformConfigHubSyncService,
    private readonly router: Router,
    private readonly store: Store<IAppState>,
    private readonly translationStorageService: TranslationLocalStorageService,
    private readonly userService: UserService,
  ) {}

  ngOnInit() {
    const groupSource = currentGroupSource(this.store, this.destroy$).pipe(
      filter((group) => !isEmpty(group)),
    );
    combineLatest([this.userState, groupSource, this.tenantState])
      .pipe(
        tap(([user, group, tenant]: [Account, Group, Tenant]) => {
          if (
            (this.group && this.group._id !== group._id) ||
            !this.group ||
            this.hasAnyFeatureEnabledChanged(group)
          ) {
            const userAdminStates = this.userService.userAdminStates();
            const isManagingPathwayForGroup =
              userAdminStates.pathwayManagerInGroupIds.includes(group._id);
            const isManagingProgramForGroup =
              userAdminStates.programManagerInGroupIds.includes(group._id);
            this.links = this.groupViewNavigationService.generateNavLinks({
              group,
              isManagingPathwayForGroup,
              isManagingProgramForGroup,
              tenant,
              user,
            });
            this.linksActive = this.generateLinksActive(this.links);
          }
          this.group = group;
          this.user = user;
          this.showGroups = false;
          this.textColor = this.mostReadableColor.transform(
            this.group.background_color[0],
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();

    groupSource
      .pipe(
        distinctUntilChanged(
          (previous, current) => previous._id === current._id,
        ),
        tap((group: Group) => {
          this.store.dispatch(FeedPopulate(group));
        }),
        concatMap((group: Group) => {
          const translationMap$ = this.localeBackend
            .getGroupLocales(group)
            .pipe(
              catchError(() => EMPTY),
              tap((result: GroupLocalesResponse) => {
                this.translationStorageService.setTranslationsMap(
                  result.groupLocales as UserContentTranslation[],
                );
              }),
            );
          const platformConfigSync$ = isTenThousandCoffeesAdmin(this.user)
            ? this.platformConfigHubSyncService.syncPlatformTemplatesWithHub(
                group,
              )
            : EMPTY;
          return combineLatest([translationMap$, platformConfigSync$]);
        }),
      )
      .subscribe();

    this.media
      .asObservable()
      .pipe(takeUntil(this.destroy$))
      .subscribe((mediaChanges: MediaChange[]) => {
        setTimeout(() => {
          const currentChange = mediaChanges[0];
          const smallSizeIndex = smallScreenSizes.indexOf(
            currentChange.mqAlias,
          );
          this.isSmallWindow = 0 <= smallSizeIndex;
        });
      });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        concatMap(() =>
          combineLatest([
            this.userState.pipe(take(1)),
            this.tenantState.pipe(take(1)),
          ]),
        ),
        tap(([user, tenant]: [Account, Tenant]) => {
          if (isEmpty(this.group)) {
            return;
          }
          const userAdminStates = this.userService.userAdminStates();
          const isManagingPathwayForGroup =
            userAdminStates.pathwayManagerInGroupIds.includes(this.group._id);
          const isManagingProgramForGroup =
            userAdminStates.programManagerInGroupIds.includes(this.group._id);
          const states = this.groupViewNavigationService.generateNavLinks({
            isManagingPathwayForGroup,
            isManagingProgramForGroup,
            tenant,
            user,
            group: this.group,
          });
          this.linksActive = this.generateLinksActive(states);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();

    combineLatest([groupSource, this.userState])
      .pipe(
        concatMap(([group, user]) =>
          this.updateGroupJoinRequestCount(group, user),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  private generateLinksActive(links: NavigationLink[]) {
    return links.reduce(
      (acc, item) => ({
        ...acc,
        [item.title]:
          this.router.isActive(item.routerLink.join('/'), {
            fragment: 'ignored',
            matrixParams: 'ignored',
            paths: 'subset',
            queryParams: 'subset',
          }) ||
          (item.subStates || []).some((state) =>
            this.router.isActive(state.routerLink.join('/'), {
              fragment: 'ignored',
              matrixParams: 'ignored',
              paths: 'subset',
              queryParams: 'subset',
            }),
          ),
      }),
      {},
    );
  }

  @HttpRequestCache()
  updateGroupJoinRequestCount(group: Group, user: Account) {
    if (!(user.isAdmin || isAdminOfGroup(user, group))) {
      return EMPTY;
    }
    return this.groupsBackend
      .getAccounts(group, {
        limit: 0,
        memberStatus: 'pending',
        status: 'active',
      })
      .pipe(
        catchError(() => EMPTY),
        tap((response: { items: Account[]; total_count: number }) => {
          this.store.dispatch(
            UpdateGroupConfig({
              group,
              update: { joinRequestsCount: response.total_count },
            }),
          );
        }),
      );
  }

  showSideNav(sideNav: MatSidenav) {
    if (sideNav.opened) {
      return sideNav.close();
    }
    return sideNav.open();
  }

  hasAnyFeatureEnabledChanged(updatedGroup: Group) {
    const hasInvitationEmailCampaignsEnabledChanged =
      this.group?.invitation_email_campaigns_enabled !==
      updatedGroup.invitation_email_campaigns_enabled;
    const hasDevelopmentProgramEnabledChanged =
      this.group?.development_programs_enabled !==
      updatedGroup.development_programs_enabled;
    const hasWelcomePageEnabledChanged =
      this.group?.community_page_information?.is_visible !==
      updatedGroup.community_page_information?.is_visible;
    const hasIntroductionsEnabledChanged =
      this.group?.features_enabled?.introductions !==
      updatedGroup.features_enabled?.introductions;
    const hasEventsEnabledChanged =
      this.group?.features_enabled?.events !==
      !updatedGroup.features_enabled?.events;
    return (
      hasInvitationEmailCampaignsEnabledChanged ||
      hasDevelopmentProgramEnabledChanged ||
      hasWelcomePageEnabledChanged ||
      hasIntroductionsEnabledChanged ||
      hasEventsEnabledChanged
    );
  }
}
